import { Link } from "@tanstack/react-router";
import { createContext, type ReactNode, useContext } from "react";
import { useWorkspaceNavigation } from "@/components/sidebar/use-workspace-navigation";
import {
	BREADCRUMB_LINK_CLASS,
	BreadcrumbPrefixContext,
	PageBreadcrumb,
} from "@/components/ui/page-breadcrumb";
import { cn } from "@/lib/utils";
import { settingsDestinationWidth } from "@/lib/workspace-navigation";

const SettingsSectionContext = createContext<{
	label: string;
	key: string;
} | null>(null);

export function WorkspaceBreadcrumbProvider({
	children,
}: {
	children: ReactNode;
}) {
	const { owner, settings, location, destinations } = useWorkspaceNavigation();
	const path = location.pathname.replace(/\/$/, "");
	// Root destination pages have no detail header to own their trail. Nested
	// pages supply their own ancestors, so a second shell trail never appears.
	const section = settings
		? (destinations.find(
				(d) =>
					d.active &&
					(path === `/${owner}/settings/organization` ||
						path === `/${owner}/${d.key}` ||
						(d.key === "team" &&
							["$member", "%24member"].some(
								(key) =>
									path === `/${owner}/${key}` ||
									path.startsWith(`/${owner}/${key}/-/`),
							))),
			) ?? null)
		: null;
	const prefix =
		settings && owner
			? [
					<Link
						key="settings"
						to="/$owner/settings"
						params={{ owner }}
						className={BREADCRUMB_LINK_CLASS}
					>
						Settings
					</Link>,
				]
			: [];
	return (
		<BreadcrumbPrefixContext.Provider value={prefix}>
			<SettingsSectionContext.Provider value={section}>
				{children}
			</SettingsSectionContext.Provider>
		</BreadcrumbPrefixContext.Provider>
	);
}

export function SettingsSectionBreadcrumb() {
	const section = useContext(SettingsSectionContext);
	// Pages pad the shell and centre their column inside that padding (the Team
	// list is uncapped), so the trail does the same to start where they do.
	return section ? (
		<div className="w-full shrink-0 px-4 pt-3 pb-1 lg:px-6">
			<div
				className={cn("mx-auto w-full", settingsDestinationWidth(section.key))}
			>
				<PageBreadcrumb crumbs={[]} current={section.label} />
			</div>
		</div>
	) : null;
}
